import {
  CatalogVariantFragmentFragment,
  StrictSalesDisplayTypeFeatureFlag,
} from '@notino/shared/definitions/types';

import { AttributeTypes, IPrice } from '@containers/ProductListing/model';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';

import { useResolvedVoucherDiscount } from '../../ProductBaseInfo/VoucherDiscount/useResolvedVoucherDiscount';

/** Converts a GraphQL price object (with nullable fields) to IPrice */
const toIPrice = (price: {
  value: number | null;
  currency: string | null;
  tax?: number | null;
}): IPrice => ({
  value: price.value ?? 0,
  currency: price.currency ?? undefined,
});

import {
  getDiscountPercentage,
  useGetBetterOfferText,
  useGetOriginalPriceTooltip,
  useGetYouSaveText,
  useRecentPriceTooltip,
} from './utils';

// https://gitlab.notino.com/web/nushopweb/-/blob/master/docs/diagrams/price-logic.md
interface IReturnValue {
  discountText?: string;
  topPrice?: {
    value: IPrice;
    isCrossed: boolean;
    tooltip: JSX.Element;
    actionPercentageFromRrpAbTest?: boolean;
  };
  bottomRecentPrice?: { value: IPrice; tooltip: JSX.Element };

  /** Indicates whether converted/recommended/maxHistorical price is shown. */
  rrpShown: boolean;
  minimalPriceShown: boolean;
}

export type UsePriceLogicParameters = Pick<
  CatalogVariantFragmentFragment,
  'attributes' | 'price' | 'originalPrice' | 'recentMinPrice'
>;

export const AB_TEST_ORIGINAL_PRICE_DISCOUNT_PERCENTAGE_THRESHOLD = 3;

export const usePriceLogic = ({
  attributes,
  price,
  originalPrice,
  recentMinPrice,
}: UsePriceLogicParameters): IReturnValue => {
  const getYouSaveText = useGetYouSaveText();
  const getBetterOfferText = useGetBetterOfferText();
  const recentPriceTooltip = useRecentPriceTooltip();
  const getOriginalPriceTooltip = useGetOriginalPriceTooltip(attributes);
  const {
    strict_sales_display_type,
    experiment_pd_action_percentage_from_rrp,
  } = useFeatureFlags();

  const isAction = attributes?.[AttributeTypes.Action];
  const isClearance = attributes?.[AttributeTypes.Clearance];
  const isActionOrClearance = isAction || isClearance;
  const hasVoucher = !!useResolvedVoucherDiscount({ attributes });

  if (experiment_pd_action_percentage_from_rrp && isActionOrClearance) {
    const isDiffFromRrpSignificant =
      !!originalPrice?.value &&
      getDiscountPercentage(price.value ?? 0, originalPrice.value) >=
        AB_TEST_ORIGINAL_PRICE_DISCOUNT_PERCENTAGE_THRESHOLD;

    if (isDiffFromRrpSignificant) {
      return {
        minimalPriceShown: !!recentMinPrice?.value,
        rrpShown: true,
        bottomRecentPrice: recentMinPrice
          ? {
              value: toIPrice(recentMinPrice),
              tooltip: recentPriceTooltip,
            }
          : undefined,
        topPrice: {
          actionPercentageFromRrpAbTest: true,
          value: toIPrice(originalPrice),
          isCrossed: true,
          tooltip: getOriginalPriceTooltip(originalPrice.type ?? ''),
        },
      };
    }

    if (recentMinPrice?.isActionEligible) {
      return {
        minimalPriceShown: true,
        rrpShown: false,
        topPrice: {
          actionPercentageFromRrpAbTest: true,
          value: toIPrice(recentMinPrice),
          isCrossed: true,
          tooltip: recentPriceTooltip,
        },
      };
    }
  }

  if (isAction || isClearance) {
    if (recentMinPrice?.isActionEligible) {
      return {
        minimalPriceShown: true,
        rrpShown: false,
        discountText: isAction
          ? undefined
          : getYouSaveText(price.value ?? 0, recentMinPrice.value ?? 0),
        topPrice: isAction
          ? undefined
          : {
              value: toIPrice(recentMinPrice),
              isCrossed: true,
              tooltip: recentPriceTooltip,
            },
        bottomRecentPrice: isAction
          ? {
              value: toIPrice(recentMinPrice),
              tooltip: recentPriceTooltip,
            }
          : undefined,
      };
    }

    if (recentMinPrice) {
      return {
        rrpShown: false,
        minimalPriceShown: true,
        bottomRecentPrice: {
          value: toIPrice(recentMinPrice),
          tooltip: recentPriceTooltip,
        },
      };
    }

    return {
      rrpShown: false,
      minimalPriceShown: false,
    };
  }

  if (hasVoucher) {
    if (
      strict_sales_display_type ===
      StrictSalesDisplayTypeFeatureFlag.ShowBottomPriceWhenVoucher
    ) {
      return recentMinPrice
        ? {
            minimalPriceShown: true,
            rrpShown: false,
            bottomRecentPrice: {
              value: toIPrice(recentMinPrice),
              tooltip: recentPriceTooltip,
            },
          }
        : { minimalPriceShown: false, rrpShown: false };
    }

    if (
      strict_sales_display_type ===
      StrictSalesDisplayTypeFeatureFlag.ShowBottomPriceAndTopPriceWhenVoucher
    ) {
      if (recentMinPrice) {
        return originalPrice?.value
          ? {
              minimalPriceShown: true,
              rrpShown: true,
              bottomRecentPrice: {
                value: toIPrice(recentMinPrice),
                tooltip: recentPriceTooltip,
              },
              topPrice: {
                value: toIPrice(originalPrice),
                isCrossed: false,
                tooltip: getOriginalPriceTooltip(originalPrice.type ?? ''),
              },
              discountText: getBetterOfferText(
                price.value ?? 0,
                originalPrice.value
              ),
            }
          : {
              minimalPriceShown: true,
              rrpShown: false,
              bottomRecentPrice: {
                value: toIPrice(recentMinPrice),
                tooltip: recentPriceTooltip,
              },
            };
      }

      return originalPrice?.value
        ? {
            minimalPriceShown: false,
            rrpShown: true,
            topPrice: {
              value: toIPrice(originalPrice),
              isCrossed: false,
              tooltip: getOriginalPriceTooltip(originalPrice.type ?? ''),
            },
            discountText: getBetterOfferText(
              price.value ?? 0,
              originalPrice.value
            ),
          }
        : {
            minimalPriceShown: false,
            rrpShown: false,
          };
    }
  }

  return originalPrice?.value
    ? {
        rrpShown: true,
        minimalPriceShown: false,
        topPrice: {
          value: toIPrice(originalPrice),
          isCrossed: false,
          tooltip: getOriginalPriceTooltip(originalPrice.type ?? ''),
        },
        discountText: getBetterOfferText(price.value ?? 0, originalPrice.value),
      }
    : { minimalPriceShown: false, rrpShown: false };
};
